export const sidebarCopy = {
  sectionLabel: "Workbench",
  heading: "Clause tools",
  ask: "Ask a question",
  askSubtitle: "Plain language lookup",
  library: "Clause library",
  librarySubtitle: "AS3000 structure",
  saved: "Saved answers",
  savedSubtitle: "Job notes and refs",
  savedCountSingular: "job note",
  savedCountPlural: "job notes",
  admin: "Admin",
  adminSubtitle: "Users and audit",
  legacySearch: "Search",
  recentQueriesLabel: "Recent queries",
  recentQueriesEmpty: "No searches yet. Your latest queries will appear here."
} as const;

export const libraryCopy = {
  heading: "Clause library",
  body: "Quick structure notes for the clause paths Jim is already referencing.",
  askFromClause: "Ask from clause",
  listLabel: "Clause list",
  selectedLabel: "Selected clause",
  askJim: "Ask Jim",
  saveNote: "Save note",
  noteQuestionPrefix: "Clause note:",
  explainPrefix: "Explain", filterPlaceholder: "Filter clauses", loading: "Loading clauses", loadingDetail: "Loading clause", licensedPointer: "Refer to this clause in your licensed copy of AS3000.", jimReadLabel: "Jim's read (guidance only)", standardLabel: "AS/NZS 3000", figureFallback: "Image missing. Please refer to your copy of AS3000", copyText: "Copy as text", copyMarkdown: "Copy as Markdown", copied: "Copied", copyFailed: "Copy failed", previous: "Previous", next: "Next", children: "Child clauses", related: "Related clauses", unresolvedReferences: "Other references", referenceTypeLabels: { clause: "Clause", section: "Section", appendix: "Appendix", external: "External standard" }, assets: "Figures and tables", breadcrumbs: "Breadcrumbs", filterMatches: "Matched in", filterFailed: "Unable to filter clauses", matchedInLabels: { number: "Clause number", title: "Title", keyword: "Keyword", verbatim: "Clause text" }, reference: "Reference", citedBy: "Cited by", curated: "Curated",
  screenGrab: "Copy screen grab", screenGrabbed: "Image copied", screenGrabFailed: "Image copy failed", screenGrabFilename: "ask-jim-clause-grab",
  reportIssue: "Report an issue", reportIssueSent: "Report sent", reportIssueFailed: "Could not send report",
  reportDialogHeading: "Report a problem with this record", reportIssueType: "Issue type", reportNote: "Note", reportNotePlaceholder: "Tell James what looks wrong", reportSubmit: "Send report", reportCancel: "Cancel",
  issueTypeLabels: {
    text_jumbled: "Text is jumbled or run together",
    text_missing: "Text is missing",
    image_wrong: "Wrong or missing figure/table image",
    image_cropped: "Image is cropped wrongly",
    wrong_reference: "Wrong page number or clause number",
    bad_link: "A cross-reference link is wrong or missing",
    jim_read_wrong: "Jim's read is wrong or misleading"
  },
  pagePrefix: "Page", appendixPrefix: "Appendix", sectionPrefix: "Section", assetPartPrefix: "part",
  assetZoomControls: "Image zoom controls", currentZoom: "Current zoom", zoomIn: "Zoom in", zoomOut: "Zoom out", fitImage: "Fit", openImage: "Open enlarged image",
  enlargedImage: "Enlarged image", closeEnlargedImage: "Close enlarged image", panImage: "Zoomed image. Scroll or drag to pan.",
  referenceSubviewLabel: "Reference sub-view", subviewBack: "Back", subviewForward: "Forward", makeSubviewMain: "Make this the main clause", closeSubview: "Close",
  informativeLabel: "Informative", deletedLabel: "Deleted from the Standard",
  amendmentPrefix: "Amendment", jurisdictionLabels: { AU: "Australia only", NZ: "New Zealand only", "AU-NZ": "Australia and New Zealand" } as Record<string, string>,
  headingOnly: "This clause is a heading. Its requirements sit in the child clauses below.",
  reviewFlag: "Flagged for extraction review — admin only, not a content warning.",
  chipsLabel: "Show", chips: { all: "All", clause: "Clauses", figure: "Figures", table: "Tables" } as Record<string, string>,
  kindLabels: { figure: "Figure", table: "Table", image: "Image" } as Record<string, string>,
  imageMissingShort: "No image",
  recordSingular: "record", recordPlural: "records",
  filterEmpty: "Nothing matches that filter.",
  empty: "No clause records have been imported yet.",
  navigationFailed: "Unable to load the clause list",
  detailFailed: "Unable to load that record",
  ownedBy: "Belongs to", referencedBy: "Referenced by"
} as const;

export const savedCopy = {
  heading: "Saved answers",
  body: "Job notes you keep from clause lookups in this session.",
  askAnother: "Ask another",
  remove: "Remove",
  emptyLoader: "Nothing saved yet",
  emptyHeading: "No saved answers yet",
  emptyBody: "Use the save button on a result or clause note to keep it here while you work."
} as const;

export const searchCopy = {
  desktopHeading: "Ask in plain term. I'll try find the relevant clauses.",
  mobileHeading: "What needs checking?",
  mobileBody: "Ask it like you would on site. Jim will keep the answer clause-first.",
  placeholder: "Can I install a GPO within 300mm of a laundry tub?",
  submit: "Ask Jim",
  modeLabel: "Mode",
  modes: {
    plain: "Plain",
    strict: "Strict",
    teach: "Teach"
  },
  initialResultsHeading: "Ask a question to find the relevant clauses.",
  initialResultsBody: "Jim will point you back to the clause number and page in your licensed copy.",
  noResultsFallback: "No matching clauses found. Try different terms or a clause number.",
  confidenceHigh: "High confidence",
  confidenceMedium: "Medium confidence",
  confidenceLow: "Low confidence",
  lowConfidenceHedge: "Not confident on this one — check the clause yourself before relying on it.",
  noReadPointer: "No plain-language read yet — see clause {clauseNumber} in your copy of AS3000.",
  matchReasonLabel: "Why this matched:",
  saveClauseLabel: "Save clause",
  openClauseLabel: "Open clause",
  feedbackUpLabel: "This result helped",
  feedbackDownLabel: "This result didn't help",
  feedbackGroupLabel: "Result helpfulness",
  searchDisclaimer: "Jim's reads are guidance only. The published AS/NZS 3000 standard is the reference — always verify against your licensed copy.",
  fieldTipLabel: "Field tip",
  fieldTipHeading: "Jim's site note",
  clauseBadge: "AS3000",
  siteNoteHeading: "Measure planes separately.",
  siteNoteBody: "If a clause says 0.5m from the edge of the vertical plane, measure a straight flat 500mm line away from that plane. Don't measure diagonally from the container edge to the electrical item.",
  whyLabel: "Why this matters",
  whyItems: [
    "Horizontal distance and vertical distance are different checks.",
    "A diagonal tape measurement can make a non-compliant position look compliant."
  ],
  recentChecksHeading: "Recent checks",
  searchFailure: "Search failed"
} as const;
